import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type { UserWithRoles } from '@dcc-erp/shared';

import { AppModal } from '../components/AppModal';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useAdminUsers,
  useAssignUserRoles,
  useCompanies,
  useCompanyUsers,
  useCreateAdminUser,
  useDirectorySearch,
  useRoles,
  useUpdateAdminUser,
} from '../lib/queries';

import type { AdminUser } from '../lib/queries';

export const Route = createFileRoute('/settings/users')({ component: UsersPageInner });

export function UsersPageInner() {
  const companyId = useActiveCompanyId();
  const { data: users = [], isPending, error } = useAdminUsers();
  const { data: companyUsers = [] } = useCompanyUsers(companyId);
  const updateUser = useUpdateAdminUser();

  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editingRoles, setEditingRoles] = useState<UserWithRoles | null>(null);
  const [adding, setAdding] = useState(false);

  return (
    <div className="page">
      <PageHead
        crumbs={['ระบบ', 'ผู้ใช้ & บทบาท']}
        title="ผู้ใช้ & บทบาท (Users & Roles)"
        actions={<HelpButton guide="settings-users" />}
      />
      <div style={{ marginBottom: 12 }}>
        <button className="btn primary" onClick={() => setAdding(true)}>
          + เพิ่มผู้ใช้
        </button>
      </div>
      <div className="tbl-shell">
        <table className="tbl">
          <thead>
            <tr>
              <th>อีเมล</th>
              <th>ชื่อ</th>
              <th>สถานะ</th>
              <th>บริษัทที่เข้าถึง</th>
              <th>บทบาท global</th>
              <th style={{ width: 160 }} />
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={6} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                >
                  โหลดล้มเหลว: {error.message}
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const companyUserRow = companyUsers.find((cu) => cu.id === u.id);
                return (
                  <tr key={u.id}>
                    <td className="code">{u.email}</td>
                    <td>{u.displayName}</td>
                    <td>
                      <span
                        style={{
                          color: u.isActive ? 'var(--positive, green)' : 'var(--negative, red)',
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      >
                        {u.isActive ? 'ใช้งาน' : 'ปิด'}
                      </span>
                    </td>
                    <td className="muted">{u.allowedCompanyIds.length} บริษัท</td>
                    <td className="muted">{u.globalRoleCodes.join(', ') || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn sm" onClick={() => setEditingUser(u)}>
                          แก้ไข
                        </button>
                        <button
                          className="btn sm ghost"
                          data-testid={`toggle-active-${u.id}`}
                          disabled={updateUser.isPending}
                          onClick={() =>
                            void updateUser.mutateAsync({
                              id: u.id,
                              data: { isActive: !u.isActive },
                            })
                          }
                        >
                          {u.isActive ? 'ปิด' : 'เปิด'}
                        </button>
                        {companyId && companyUserRow && (
                          <button
                            className="btn sm"
                            onClick={() => setEditingRoles(companyUserRow)}
                          >
                            แก้บทบาท
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {adding && <AddUserModal onClose={() => setAdding(false)} />}
      {editingUser && <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} />}
      {editingRoles && companyId && (
        <AssignModal
          companyId={companyId}
          user={editingRoles}
          onClose={() => setEditingRoles(null)}
        />
      )}
    </div>
  );
}

// ─── AddUserModal ─────────────────────────────────────────────────────────────

function AddUserModal({ onClose }: { onClose: () => void }) {
  const { data: companies = [] } = useCompanies();
  const { data: roles = [] } = useRoles();
  const createUser = useCreateAdminUser();

  const companyOptions = companies.filter((c): c is typeof c & { id: string } => !!c.id);

  const [q, setQ] = useState('');
  const { data: results = [] } = useDirectorySearch(q);

  const [entraOid, setEntraOid] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [allowedCompanyIds, setAllowedCompanyIds] = useState<string[]>([]);
  const [defaultCompanyId, setDefaultCompanyId] = useState('');
  const [initialRoleIds, setInitialRoleIds] = useState<string[]>([]);

  function pick(u: {
    oid: string;
    displayName: string | null;
    mail: string | null;
    userPrincipalName: string | null;
  }) {
    setEntraOid(u.oid);
    setEmail(u.mail ?? u.userPrincipalName ?? '');
    setDisplayName(u.displayName ?? '');
  }

  function toggleCompany(id: string) {
    setAllowedCompanyIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (!next.includes(defaultCompanyId)) {
        setDefaultCompanyId(next[0] ?? '');
      }
      return next;
    });
  }

  function toggleRole(id: string) {
    setInitialRoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const noEmail = entraOid !== '' && email === '';
  const canSubmit =
    entraOid !== '' && email !== '' && allowedCompanyIds.length > 0 && !createUser.isPending;

  async function submit() {
    await createUser.mutateAsync({
      entraOid,
      email,
      displayName,
      allowedCompanyIds,
      defaultCompanyId,
      initialRoleIds: initialRoleIds.length > 0 ? initialRoleIds : undefined,
    });
    onClose();
  }

  return (
    <AppModal
      title="เพิ่มผู้ใช้ใหม่"
      subtitle="ค้นหาผู้ใช้จาก Entra directory แล้วกำหนดสิทธิ์"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="btn primary" disabled={!canSubmit} onClick={() => void submit()}>
            สร้างผู้ใช้
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Directory search */}
        <div style={{ fontSize: 13 }}>
          <div style={{ marginBottom: 6, fontWeight: 600 }}>ค้นหาจาก Entra Directory</div>
          <input
            className="input"
            placeholder="พิมพ์ชื่อหรืออีเมล (≥2 ตัวอักษร)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {results.length > 0 && (
            <div
              style={{
                border: '1px solid var(--border, #ddd)',
                borderRadius: 4,
                marginTop: 4,
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {results.map((u) => (
                <button
                  key={u.oid}
                  type="button"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '6px 10px',
                    textAlign: 'left',
                    background: entraOid === u.oid ? 'var(--accent-light, #e8f0fe)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                  onClick={() => pick(u)}
                >
                  <span style={{ fontWeight: 500 }}>{u.displayName}</span>{' '}
                  <span className="muted">{u.mail ?? u.userPrincipalName}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected user info */}
        {entraOid && (
          <div
            style={{
              fontSize: 13,
              padding: '8px 10px',
              background: 'var(--surface2, #f5f5f5)',
              borderRadius: 4,
            }}
          >
            <div>
              <span style={{ fontWeight: 600 }}>ชื่อ: </span>
              {displayName || '(ไม่มีชื่อ)'}
            </div>
            <div>
              <span style={{ fontWeight: 600 }}>อีเมล: </span>
              {email || <span style={{ color: 'var(--negative, red)' }}>ไม่มีอีเมล</span>}
            </div>
          </div>
        )}

        {noEmail && (
          <p style={{ color: 'var(--negative, red)', fontSize: 12, margin: 0 }}>
            ผู้ใช้นี้ไม่มีอีเมล — ไม่สามารถสร้างบัญชีได้
          </p>
        )}

        {/* Company access checkboxes */}
        <div style={{ fontSize: 13 }}>
          <div style={{ marginBottom: 6, fontWeight: 600 }}>บริษัทที่เข้าถึง</div>
          {companyOptions.map((c) => (
            <label
              key={c.id}
              style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}
            >
              <input
                type="checkbox"
                data-testid={`company-${c.id}`}
                checked={allowedCompanyIds.includes(c.id)}
                onChange={() => toggleCompany(c.id)}
              />
              <span className="code">{c.code}</span>
              <span className="muted">{c.nameTh}</span>
            </label>
          ))}
        </div>

        {/* Initial roles (optional) */}
        {roles.length > 0 && (
          <div style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 6, fontWeight: 600 }}>บทบาทเริ่มต้น (ไม่บังคับ)</div>
            {roles.map((r) => (
              <label
                key={r.id}
                style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}
              >
                <input
                  type="checkbox"
                  checked={initialRoleIds.includes(r.id)}
                  onChange={() => toggleRole(r.id)}
                />
                <span className="code">{r.code}</span>
                <span className="muted">{r.nameTh}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </AppModal>
  );
}

// ─── EditUserModal ────────────────────────────────────────────────────────────

function EditUserModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const { data: companies = [] } = useCompanies();
  const updateUser = useUpdateAdminUser();

  // Only include companies that have an id (seed const rows lack id)
  const companyOptions = companies.filter((c): c is typeof c & { id: string } => !!c.id);

  const [displayName, setDisplayName] = useState(user.displayName);
  const [allowedCompanyIds, setAllowedCompanyIds] = useState<string[]>(user.allowedCompanyIds);
  const [defaultCompanyId, setDefaultCompanyId] = useState(user.defaultCompanyId);
  const [isActive, setIsActive] = useState(user.isActive);

  function toggleCompany(id: string) {
    setAllowedCompanyIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      // If default company was removed, reset to first allowed or empty
      if (!next.includes(defaultCompanyId)) {
        setDefaultCompanyId(next[0] ?? '');
      }
      return next;
    });
  }

  async function save() {
    await updateUser.mutateAsync({
      id: user.id,
      data: {
        displayName,
        allowedCompanyIds,
        defaultCompanyId: defaultCompanyId || undefined,
        isActive,
      },
    });
    onClose();
  }

  const isPristine =
    displayName === user.displayName &&
    isActive === user.isActive &&
    defaultCompanyId === user.defaultCompanyId &&
    allowedCompanyIds.length === user.allowedCompanyIds.length &&
    allowedCompanyIds.every((id) => user.allowedCompanyIds.includes(id));

  return (
    <AppModal
      title={`แก้ไขผู้ใช้: ${user.email}`}
      subtitle="แก้ไขชื่อแสดง, บริษัทที่เข้าถึง, บริษัทเริ่มต้น และสถานะ"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={updateUser.isPending || isPristine || allowedCompanyIds.length === 0}
            onClick={() => void save()}
          >
            บันทึก
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          <span>ชื่อแสดง</span>
          <input
            className="input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>

        <div style={{ fontSize: 13 }}>
          <div style={{ marginBottom: 6, fontWeight: 600 }}>บริษัทที่เข้าถึง</div>
          {companyOptions.map((c) => (
            <label
              key={c.id}
              style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}
            >
              <input
                type="checkbox"
                checked={allowedCompanyIds.includes(c.id)}
                onChange={() => toggleCompany(c.id)}
              />
              <span className="code">{c.code}</span>
              <span className="muted">{c.nameTh}</span>
            </label>
          ))}
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          <span>บริษัทเริ่มต้น</span>
          <select
            className="input"
            value={defaultCompanyId}
            onChange={(e) => setDefaultCompanyId(e.target.value)}
          >
            {companyOptions
              .filter((c) => allowedCompanyIds.includes(c.id))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.nameTh}
                </option>
              ))}
          </select>
        </label>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span>ใช้งาน (Active)</span>
        </label>
      </div>
    </AppModal>
  );
}

// ─── AssignModal (per-company role assignment — unchanged) ─────────────────────

function AssignModal({
  companyId,
  user,
  onClose,
}: {
  companyId: string;
  user: UserWithRoles;
  onClose: () => void;
}) {
  const { data: roles = [], isPending: rolesPending } = useRoles();
  const assign = useAssignUserRoles(companyId);

  // Derived pre-checked set — re-computes whenever the role catalog arrives (cold-cache
  // safe: the useState initializer would run before useRoles resolves, leaving every
  // box unchecked and risking an accidental "save empty" that strips the user's roles).
  const defaultSelected = useMemo(
    () => new Set(roles.filter((r) => user.roleCodes.includes(r.code)).map((r) => r.id)),
    [roles, user.roleCodes],
  );
  // User's explicit toggle overrides on top of the query-derived default.
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());
  const selected = useMemo(() => {
    const s = new Set(defaultSelected);
    overrides.forEach((on, id) => (on ? s.add(id) : s.delete(id)));
    return s;
  }, [defaultSelected, overrides]);

  function toggle(id: string) {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(id, !selected.has(id));
      return next;
    });
  }

  async function save() {
    await assign.mutateAsync({ userId: user.id, data: { roleIds: [...selected] } });
    onClose();
  }

  return (
    <AppModal
      title={`บทบาทของ ${user.email}`}
      subtitle="กำหนดบทบาทเฉพาะบริษัทนี้ (บทบาท global จัดการแยก)"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={rolesPending || assign.isPending}
            onClick={() => void save()}
          >
            บันทึก
          </button>
        </>
      }
    >
      {roles.map((r) => (
        <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
          <span className="code">{r.code}</span> <span className="muted">{r.nameTh}</span>
        </label>
      ))}
    </AppModal>
  );
}
